import type { ReportTable, ReportTimeFilter } from '@erp/shared-types';

/**
 * Custom division ordering for reports per client requirements.
 * Divisions not in this list will appear alphabetically at the end.
 */
export const DIVISION_PRIORITY_ORDER = [
  'Nedco Ontario',
  'Nedco Quebec',
  'Nedco West',
  'Rexel Canada Electrical Inc Atlantic Div',
  'Westburne Midwest',
  'Westburne Ontario',
  'Westburne Quebec',
  'Westburne West',
  'ORKA',
  'Rexel Utility',
  'Rexel Wire & Cable Edmonton',
  'Rexel Wire & Cable',
  'Ontario Distribution Centre',
  'Midwest Distribution Centre',
  'Lineman Testing Laboratories (LTL)',
  'Edmonton Distribution Centre',
  'Rexel Wire & Cable Kingston',
];

/**
 * Sorts report table rows according to the custom division priority order.
 * Rows not in the priority list are sorted alphabetically at the end.
 * The 'total' row always stays at the bottom.
 */
export function sortReportTableByDivision<
  T extends { rows: Array<{ id: string; label: string }> },
>(table: T): T {
  const sortedRows = [...table.rows].sort((a, b) => {
    // Total row always stays at the bottom
    if (a.id === 'total') return 1;
    if (b.id === 'total') return -1;

    const aIndex = DIVISION_PRIORITY_ORDER.indexOf(a.label);
    const bIndex = DIVISION_PRIORITY_ORDER.indexOf(b.label);

    // Both in priority list - sort by priority
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    // Only a in priority list - a comes first
    if (aIndex !== -1) return -1;
    // Only b in priority list - b comes first
    if (bIndex !== -1) return 1;
    // Neither in priority list - sort alphabetically
    return a.label.localeCompare(b.label);
  });

  return { ...table, rows: sortedRows };
}

/**
 * Compute which column indices should be visible based on time filter
 */
export function getVisibleColumnIndices(
  table: ReportTable,
  timeFilter: ReportTimeFilter,
  year?: number,
): number[] {
  const monthPattern =
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(\s+\d{4})?$/;
  const monthColumnIndices: number[] = [];
  const nonMonthColumnIndices: number[] = [];

  table.columnHeaders.forEach((header, index) => {
    if (monthPattern.test(header)) {
      monthColumnIndices.push(index);
    } else {
      nonMonthColumnIndices.push(index);
    }
  });

  // If no month columns, show all columns
  if (monthColumnIndices.length === 0) {
    return Array.from({ length: table.columnHeaders.length }, (_, i) => i);
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  // For past years, treat 'current' and 'ytd' as December (full year)
  // For current year, use the actual current month
  const effectiveMonthIndex =
    year !== undefined && year < currentYear ? 11 : now.getMonth();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Filter month columns based on time filter
  let visibleMonthIndices: number[] = [];
  switch (timeFilter) {
    case 'ytd': {
      // Show Jan through effective month (December for past years, current month for current year)
      visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
        const header = table.columnHeaders[colIndex];
        const monthMatch = header.match(
          /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/,
        );
        if (!monthMatch) return false;
        const monthName = monthMatch[1];
        const monthIdx = monthNames.indexOf(monthName);
        return monthIdx >= 0 && monthIdx <= effectiveMonthIndex;
      });
      break;
    }
    case 'current': {
      // Show only effective month (December for past years, current month for current year)
      const effectiveMonthName = monthNames[effectiveMonthIndex];
      visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
        const header = table.columnHeaders[colIndex];
        return header.startsWith(effectiveMonthName);
      });
      break;
    }
    case 'all':
    default: {
      // Show all month columns
      visibleMonthIndices = monthColumnIndices;
      break;
    }
  }

  // Combine non-month and visible month columns, maintaining order
  const allVisibleIndices: number[] = [];
  table.columnHeaders.forEach((_, index) => {
    if (
      nonMonthColumnIndices.includes(index) ||
      visibleMonthIndices.includes(index)
    ) {
      allVisibleIndices.push(index);
    }
  });

  return allVisibleIndices;
}
